import {
  FC,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useController, useFormContext } from "react-hook-form";
import { AnimatePresence } from "framer-motion";

import ProductionWysiwygModal from "./ProductionWysiwygModal";
import Button from "@/components/Button";
import Text from "@/components/Text";
import { HighlightResource, ProductionSceneResource } from "@/types/resources";
import { api } from "@/utils/api";
import { SceneFormT } from "./ProductionScriptBlock";
import { handleFormErrors } from "@/utils/helpers";
import useAlerts from "@/hooks/useAlerts";
import usePrompt from "@/hooks/usePrompt";
import CommentsModal from "./CommentsModal";
import SceneComment from "./SceneComment";
import { scrollBarStyles } from "@/utils/theme";

const WysiwygWrapper = styled.div`
  [contenteditable][placeholder]:empty:before {
    content: attr(placeholder);
    background-color: transparent;
  }
  position: relative;
  height: 1010px;
  @media print {
    height: auto;
  }
`;

const Wysisyg = styled.div`
  font-family: "Manrope";
  max-height: 978px;
  min-height: 50px;
  padding: 0 24px 0;
  outline: 0;
  font-size: 14px;
  line-height: 22px;
  overflow-y: auto;
  font-weight: 400;
  @media print {
    max-height: none;
    overflow: visible;
  }
  b {
    font-weight: 700;
  }
  em {
    user-select: none;
    cursor: pointer;
  }
  ${scrollBarStyles};
`;

const SaveButtonHolder = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 24px;
`;

const SaveButton = styled(Button)`
  margin-right: 24px;
`;

const WysiwygFooter = styled.div`
  color: ${({ theme }) => theme.colors.gray};
  display: flex;
  align-items: center;
  position: absolute;
  bottom: 0;
  left: 24px;
  svg {
    margin-right: 4px;
  }
`;

const onTextPaste = (e: any) => {
  e.preventDefault();
  const text = e.clipboardData?.getData("text/plain");
  document.execCommand("insertText", false, text);
};

export enum BreakdownType {
  LOCATION = "location",
  PROP = "prop",
  SHOPPING_ITEM = "shopping_item",
  ACTOR = "actor",
  VOICEOVER = "voiceover",
}

export const getBreakdownColor = (type: BreakdownType) => {
  switch (type) {
    case BreakdownType.LOCATION:
      return "#1C6DD0";
    case BreakdownType.PROP:
      return "#C9513D";
    case BreakdownType.SHOPPING_ITEM:
      return "#18713D";
    case BreakdownType.ACTOR:
      return "#AD64C5";
    case BreakdownType.VOICEOVER:
      return "#ED911B";
    default:
      return "#000000";
  }
};

const getBreakdownStoreUrl = (type: BreakdownType) => {
  switch (type) {
    case BreakdownType.LOCATION:
      return "/v1/production/scenes/locations";
    case BreakdownType.PROP:
      return "/v1/production/scenes/props";
    case BreakdownType.SHOPPING_ITEM:
      return "/v1/production/scenes/shopping-items";
    case BreakdownType.ACTOR:
      return "/v1/production/scenes/actors";
    case BreakdownType.VOICEOVER:
      return "/v1/production/scenes/voiceovers";
    default:
      return "";
  }
};

interface Props {
  scene: ProductionSceneResource;
  setShowSidebar: React.Dispatch<React.SetStateAction<boolean>>;
  setCommentsEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  commentsEnabled: boolean;
}

export type SelectionState = {
  color: string;
  highlightId?: number;
  breakdownType?: BreakdownType;
} | null;

interface CommentsModalStata {
  show: boolean;
  text?: string;
  element?: HTMLSpanElement;
  commentId?: number;
}

// function getSelectionHtml() {
//   var html = "";
//   if (typeof window.getSelection != "undefined") {
//     var sel = window.getSelection();
//     if (sel?.rangeCount) {
//       var container = document.createElement("div");
//       for (var i = 0, len = sel.rangeCount; i < len; ++i) {
//         container.appendChild(sel.getRangeAt(i).cloneContents());
//       }
//       html = container.innerHTML;
//     }
//   }
//   return html;
// }

const ProductionWysiwyg: FC<Props> = ({
  scene,
  setShowSidebar,
  commentsEnabled,
  setCommentsEnabled,
}) => {
  const [anchorPoint, setAnchorPoint] = useState({ x: 0, y: 0 });

  const [showModal, setShowModal] = useState(false);

  const [commentsModalState, setCommentsModalState] =
    useState<CommentsModalStata>({
      show: false,
    });

  const [selection, setSelection] = useState<Range>();

  const [isSelectionUpdatable, setIsSelectionUpdatable] = useState(false);

  const [selectedHighlightElement, setSelectedHighlightElement] =
    useState<Element>();

  const [selectionState, setSelectionState] = useState<SelectionState>(null);

  const { teamId, projectId } = useParams();

  const alerts = useAlerts();

  const { data: highlights } = useQuery<HighlightResource[]>(
    ["script_highlights", { teamId }],
    () =>
      api.get<never, HighlightResource[]>("/v1/production/highlights", {
        params: {
          "filter[team_id]": teamId,
        },
      }),
    {
      staleTime: 120 * 1000,
    }
  );

  function saveSelection() {
    if (window.getSelection) {
      const sel = window.getSelection();
      if (sel?.getRangeAt && sel.rangeCount) {
        return sel.getRangeAt(0);
      }
    }
    return null;
  }

  const wysiwygRef = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();

  const handleModalOnKeyUp = useCallback(
    (e: KeyboardEvent) => {
      if (e.key !== "Shift" && e.key !== "Delete" && e.key !== "Backspace")
        return;

      if (window.getSelection()?.toString()) {
        setAnchorPoint({
          x: 50,
          y: 50,
        });
        const savedSelection = saveSelection();

        if (savedSelection?.toString().length) {
          if (savedSelection?.toString().length < 2) {
            return;
          }
        }

        setShowModal(true);

        !!savedSelection && setSelection(savedSelection);
      }
    },
    [setAnchorPoint]
  );

  const handleModal = useCallback(
    (e: MouseEvent) => {
      const target = e.target as Element;
      if (target.tagName === "EM" || window.getSelection()?.toString()) {
        const rect = wysiwygRef?.current?.getBoundingClientRect();
        let x = e.clientX - rect!.left;
        let y = e.clientY - rect!.top;
        setAnchorPoint({ x: x, y: y });
        e.stopPropagation();
        if (target.tagName === "EM") {
          setIsSelectionUpdatable(true);
          setSelectedHighlightElement(target);
        } else {
          const savedSelection = saveSelection();
          !!savedSelection && setSelection(savedSelection);
          setSelectedHighlightElement(undefined);
          if (savedSelection?.toString().length) {
            if (savedSelection?.toString().length < 2) {
              return;
            }
          }
        }

        setShowModal(true);
      }
    },
    [setAnchorPoint, setShowModal]
  );

  const {
    setError,
    handleSubmit,
    clearErrors,
    control,
    formState: { isSubmitting, isDirty },
  } = useFormContext<SceneFormT>();

  const {
    field: { onChange },
  } = useController({
    control,
    name: "content",
  });

  useEffect(() => {
    const element = wysiwygRef.current;

    if (!element) return;

    element.addEventListener("click", handleModal);
    element.addEventListener("keyup", handleModalOnKeyUp);
    element.addEventListener("paste", onTextPaste);

    const observerCallback = () => {
      onChange(element.innerHTML);
    };

    const observer = new MutationObserver(observerCallback);

    observer.observe(element, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      element.removeEventListener("click", handleModal);
      element.removeEventListener("keyup", handleModalOnKeyUp);
      element.removeEventListener("paste", onTextPaste);
      observer.disconnect();
    };
  }, [handleModal, handleModalOnKeyUp, onChange]);

  const onModalClose = () => {
    setShowModal(false);
    setIsSelectionUpdatable(false);
    setSelectionState(null);
    window.getSelection()?.removeAllRanges();
    setSelectedHighlightElement(undefined);
  };

  const saveBreakdown = async (type: BreakdownType, text: string) => {
    try {
      const url = getBreakdownStoreUrl(type);

      await api.post(url, {
        ...([
          BreakdownType.LOCATION,
          BreakdownType.PROP,
          BreakdownType.SHOPPING_ITEM,
        ].includes(type) && {
          title: text,
        }),
        ...(BreakdownType.VOICEOVER === type && {
          element: text,
        }),
        ...(BreakdownType.ACTOR === type && {
          role: text,
        }),
        production_scene_id: scene.id,
      });

      onSave(false);
    } catch (_) {
      alerts.error("Failed to save breakdown!");
    }
  };

  const createHighlightElement = (textToReplace: string) => {
    const element = document.createElement("em");

    element.setAttribute("contenteditable", "false");

    if (selectionState!.highlightId) {
      element.setAttribute(
        "data-highlight",
        selectionState!.highlightId.toString()
      );
    } else {
      element.setAttribute("data-breakdown", selectionState!.breakdownType!);
      saveBreakdown(selectionState!.breakdownType!, textToReplace);
    }

    element.style.background = selectionState!.color;

    element.appendChild(document.createTextNode(textToReplace));

    return element;
  };

  const replaceSelectedText = () => {
    const selection = window.getSelection();
    if (selection) {
      const text = selection.toString();
      const range = selection.getRangeAt(0);
      range.deleteContents();

      const element = createHighlightElement(text);

      range.insertNode(element);
    }
  };

  const onSelectionRemove = () => {
    if (!selectedHighlightElement?.textContent) return;

    selectedHighlightElement.replaceWith(selectedHighlightElement.textContent);
  };

  const onUpdateSelectedText = (newText: string) => {
    const element = createHighlightElement(newText);

    selectedHighlightElement?.replaceWith(element);
  };

  usePrompt(
    isDirty,
    "You have unsaved changes. Are you sure you would like to proceed?",
    "Unsaved changes will be lost"
  );

  const onSave = (
    showSuccessModal = true,
    e?: ReactMouseEvent<HTMLButtonElement>
  ) => {
    clearErrors();
    handleSubmit(async ({ name }) => {
      try {
        const highlights = Array.from(
          wysiwygRef.current?.querySelectorAll("[data-highlight]") ?? []
        ).map((el) => {
          const htmlEl = el as HTMLElement;

          return {
            id: htmlEl.dataset.highlight,
            marked_text: htmlEl.innerText,
          };
        });

        const comments = Array.from(
          wysiwygRef.current?.querySelectorAll("[data-comment]") ?? []
        ).map((el) => {
          const htmlEl = el as HTMLElement;

          return htmlEl.dataset.comment;
        });

        await api.put(`/v1/production/scenes/${scene.id}`, {
          name,
          content: wysiwygRef.current?.innerHTML ?? "",
          highlights,
          comments,
        });

        await queryClient.invalidateQueries([
          "production_projects",
          { projectId },
        ]);

        showSuccessModal &&
          alerts.success("Scene has been successfully updated", scene.name);
      } catch (err) {
        handleFormErrors<SceneFormT>(err, setError, alerts);
      }
    })(e);
  };

  const onComment = (selectedElement?: Element) => {
    if (selectedElement) {
      const text = selectedElement.textContent ?? "";

      const element = document.createElement("span");
      element.classList.add("commented");
      element.setAttribute("contenteditable", "false");
      element.innerHTML = "&nbsp;";
      element.style.fontSize = "0px";
      element.style.lineHeight = "0px";

      selectedElement.prepend(element);

      setCommentsModalState({
        element,
        text,
        show: true,
      });
      setShowSidebar(false);
      setCommentsEnabled(true);
      onModalClose();
      return;
    }

    if (window.getSelection()?.toString()) {
      const text = window.getSelection()?.toString()!;
      const range = window.getSelection()?.getRangeAt(0);

      const element = document.createElement("span");
      element.classList.add("commented");
      element.setAttribute("contenteditable", "false");
      element.innerHTML = "&nbsp;";
      element.style.fontSize = "0px";
      element.style.lineHeight = "0px";

      range?.insertNode(element);

      setCommentsModalState({
        element,
        text,
        show: true,
      });
      setShowSidebar(false);
      setCommentsEnabled(true);
    }
    setShowModal(false);
  };

  return (
    <>
      <WysiwygWrapper>
        <Wysisyg
          ref={wysiwygRef}
          contentEditable={true}
          dangerouslySetInnerHTML={{ __html: scene.content }}
          spellCheck="false"
          placeholder="Add text here"
        />

        <SaveButtonHolder>
          <SaveButton
            onClick={(e: ReactMouseEvent<HTMLButtonElement>) => onSave(true, e)}
            loading={isSubmitting}
            disabled={isSubmitting || !isDirty}
            color="colorized"
          >
            Save
          </SaveButton>
        </SaveButtonHolder>
        <WysiwygFooter>
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g opacity="0.6">
              <circle cx="6" cy="6" r="6" fill="currentColor" />
              <path
                d="M5.4375 4.30147V3.375H6.5625V4.30147H5.4375ZM5.4375 8.625V4.91912H6.5625V8.625H5.4375Z"
                fill="white"
              />
            </g>
          </svg>
          <Text $type="smallNormal" $color="gray">
            Hit enter to start new paragraph
          </Text>
        </WysiwygFooter>
        {showModal && (
          <ProductionWysiwygModal
            onComment={onComment}
            selectionState={selectionState}
            setSelectionState={setSelectionState}
            anchorPoint={anchorPoint}
            isSelectionUpdatable={isSelectionUpdatable}
            replaceSelectedText={replaceSelectedText}
            onSelectionRemove={onSelectionRemove}
            onUpdateSelectedText={onUpdateSelectedText}
            onModalClose={onModalClose}
            selectedHighlightElement={selectedHighlightElement}
            selection={selection}
            highlights={highlights}
          />
        )}
        {commentsEnabled &&
          scene.discusions?.map((discusion) => (
            <SceneComment
              author={discusion.author}
              key={discusion.id}
              commentId={discusion.id}
              onClick={() => {
                setCommentsModalState({
                  show: true,
                  commentId: discusion.id,
                  text: discusion.phrase,
                });
                setShowSidebar(false);
              }}
            />
          ))}
      </WysiwygWrapper>
      <AnimatePresence>
        {commentsModalState.show && (
          <CommentsModal
            onClose={() => {
              setCommentsModalState({ show: false });
              setShowSidebar(true);
            }}
            sceneId={scene.id}
            discusion={
              commentsModalState.commentId
                ? scene.discusions?.find(
                    (d) => d.id === commentsModalState.commentId
                  )
                : undefined
            }
            text={commentsModalState.text}
            element={commentsModalState.element}
            onDiscusionCreated={(id: number) => {
              setCommentsModalState({
                show: true,
                commentId: id,
              });
            }}
            saveContent={() => onSave(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default ProductionWysiwyg;
